<template>
  <div id="raise-table">
    <SmallLoader v-if="smallLoading && table === 'raisingTable'" />
    <div v-else class="raising-container">
      <!-- Companies Grid -->
      <div v-if="tableRows.length > 0" class="raising-grid">
        <router-link
          v-for="(token, index) in visibleTokens"
          :key="'token-' + index"
          :to="{
            name: 'TokenOverview',
            params: { symbol: token.symbol, id: token._id },
          }"
          class="raising-card"
        >
          <!-- Card Header -->
          <div class="card-header">
            <div class="token-avatar">
              <img 
                :src="token.image" 
                :alt="token.name"
                @error="handleImageError"
              />
            </div>
            <div class="header-badges">
              <span v-if="isNewListing(token)" class="status-badge status-badge--live">
                <span class="status-dot"></span>
                Live
              </span>
              <DealTypeBadge :badge="viewOf(token).badge" />
              <ShariaBadge :token="token" size="sm" />
            </div>
          </div>

          <!-- Token Info -->
          <div class="token-details">
            <h3 class="token-name">{{ capitalizeEachWord(token.name) }}</h3>
            <span class="token-symbol">{{ token.symbol }}</span>
            <!-- What this deal is about in one figure: a note's yield, a metal's or a stock's
                 price, a fund's rate. -->
            <div v-if="viewOf(token).hero" class="token-hero">
              <span class="hero-value">{{ viewOf(token).hero.value }}</span>
              <span class="hero-unit">{{ viewOf(token).hero.unit }}</span>
              <span v-if="viewOf(token).change" class="hero-change" :class="viewOf(token).change.tone">{{ viewOf(token).change.text }}</span>
            </div>
            <!-- Only for offerings with a period. A continuously filling deal has nothing to
                 say here that the funded bar below does not already show. -->
            <div v-if="hasOfferPeriod(token)" class="token-offer-state">
              <OfferingStatusBadge :token="token" />
              <span class="token-closes">
                Closes {{ formatOfferMoment(token.offer_closes_at, token.market_timezone) }}
              </span>
            </div>
          </div>

          <!-- Stats Row -->
          <!-- Metals and NGX stocks sell at the market price with no goal to reach, so they show
               what is available rather than a target and how far it is funded. -->
          <div class="stats-row">
            <div v-if="byAvailability(token)" class="stat-block">
              <span class="stat-value">{{ availableOf(token) }}</span>
              <span class="stat-label">Available</span>
            </div>
            <div v-else class="stat-block">
              <span class="stat-value">{{ formatCompact(token.raise_amount, token.currency) }}</span>
              <span class="stat-label">Target</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-block">
              <span class="stat-value">{{ formatCompact(token.min_trade?.buy || 0, token.currency) }}</span>
              <span class="stat-label">Min.</span>
            </div>
            <template v-if="!byAvailability(token)">
              <div class="stat-divider"></div>
              <div class="stat-block">
                <span class="stat-value stat-value--highlight">{{ getProgress(token) }}%</span>
                <span class="stat-label">Funded</span>
              </div>
            </template>
          </div>

          <!-- Progress Bar -->
          <div v-if="!byAvailability(token)" class="progress-container">
            <div class="progress-track">
              <div class="progress-fill" :style="{ width: getProgress(token) + '%' }"></div>
            </div>
          </div>

          <!-- Action -->
          <div class="card-footer">
            <span class="action-cta">
              <span class="cta-text">View Details</span>
              <svg class="cta-icon" viewBox="0 0 16 16" fill="none">
                <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </div>
        </router-link>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state">
        <EmptyState>
          <template v-slot:message>
            <p class="empty-text" v-if="hasSearchQuery">
              No company with that name exists, please try a different name
            </p>
            <p class="empty-text" v-else>
              There are currently no companies raising, please try again later
            </p>
          </template>
        </EmptyState>
      </div>

      <!-- Pagination -->
      <div v-if="!limit && tableRows.length > 0" class="pagination-wrapper">
        <Pagination
          :totalPage="raisingPages"
          :currentPage="raisingPage"
          :maxVisible="5"
          @pageChange="gotoPage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import OfferingStatusBadge from "@/components/general/OfferingStatusBadge.vue";
import DealTypeBadge from "@/components/general/DealTypeBadge.vue";
import ShariaBadge from "@/components/general/ShariaBadge.vue";
import { formatOfferMoment, hasOfferPeriod } from "@/utils/offering";
import { availableLabel, listingView, raiseFigures, sellsByAvailability } from "@/utils/trade-view";
import Pagination from "@/components/navigation/Pagination";
import EmptyState from "@/components/notification/EmptyState.vue";
import TableHead from "@/components/tables/TableHeader";
import { capitalizeEachWord, formatAmountToDollar, formatCompactAmount } from "@/utils/helpers.js";
import { mapActions, mapGetters, mapState } from "vuex";

export default {
  name: "RaisingTable",

  components: { TableHead, Pagination, EmptyState, OfferingStatusBadge, DealTypeBadge, ShariaBadge },

  data() {
    return {
      tableHeaders: [
        "",
        "",
        "Amount to raise",
        "Investment Type",
        "Minimum Investment Amount",
        "Progress",
      ],
      tableRows: [],
      availableStatuses: ["Active", "Fulfilled", "Terminated"],
      table: "",
    };
  },

  computed: {
    ...mapState({
      smallLoading: (state) => state.smallLoading,
    }),
    ...mapGetters("TokenModule", [
      "companiesRaising",
      "raisingPage",
      "raisingPages",
    ]),

    /**
     * The rows actually rendered.
     *
     * `limit` is for preview placements like the Treasury overview, which shows a handful and
     * links onward. An explicit prop rather than sniffing $route.name, so the caller decides
     * how many it wants and the table stays reusable.
     */
    visibleTokens() {
      const list = this.companiesRaising || [];
      return this.limit > 0 ? list.slice(0, this.limit) : list;
    },
    // One view per card, worked out once per list rather than on every render.
    views() {
      return new Map((this.companiesRaising || []).map((token) => [token._id, listingView(token)]));
    },
  },

  props: {
    // 0 means "no cap" — the full-page listing at /dashboard/invest passes nothing.
    limit: {
      type: Number,
      required: false,
      default: 0,
    },
    hasSearchQuery: {
      type: Boolean,
      required: false,
      default: () => {
        return false;
      },
    },
  },

  mounted() {
    if (!this.hasSearchQuery) {
      this.getDetails();
    }
  },

  methods: {
    hasOfferPeriod,
    formatOfferMoment,
    ...mapActions("TokenModule", ["getCompaniesRaising"]),
    capitalizeEachWord,
    formatAmountToDollar,

    viewOf(token) {
      return this.views.get(token._id) || listingView(token);
    },
    byAvailability(token) {
      return sellsByAvailability(token);
    },
    availableOf(token) {
      return availableLabel(token);
    },

    // total_raised counts units, not money: the share of supply sold, from one set of figures.
    getProgress(token) {
      return raiseFigures(token).percent.toFixed(0);
    },

    formatCompact(value, currency = 'USD') {
      return formatCompactAmount(value || 0, currency);
    },

    getHeroClass(token) {
      const type = (token.investment_type || 'equity').toLowerCase();
      if (type.includes('debt')) return 'hero--amber';
      if (type.includes('fund')) return 'hero--cyan';
      if (type.includes('convertible')) return 'hero--purple';
      return 'hero--blue';
    },

    isNewListing(token) {
      if (!token.createdAt) return true;
      const createdDate = new Date(token.createdAt);
      const now = new Date();
      const daysDiff = (now - createdDate) / (1000 * 60 * 60 * 24);
      return daysDiff <= 30;
    },

    handleImageError(e) {
      e.target.src = require("@/assets/img/token-icon.svg");
    },

    async getDetails() {
      let limit;
      this.table = "raisingTable";
      this.$route.name === "OverviewDetails" ? (limit = 3) : (limit = 10);
      const query = { page: 1, limit: limit };
      await this.getCompaniesRaising(query);
      this.table = "";
    },

    async gotoPage(page) {
      this.table = "raisingTable";
      this.tableRows = [];
      const payload = {
        page: page,
        limit: 10,
      };
      await this.getCompaniesRaising(payload);
      this.table = "";
    },

    updateTableRow(tokens) {
      this.tableRows = tokens.map((token) => ({
        id: token._id,
        symbol: token.symbol,
        name: token.name,
      }));
    },
  },

  watch: {
    companiesRaising(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.updateTableRow(this.companiesRaising);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#raise-table {
  .raising-container {
    width: 100%;
  }

  .raising-grid {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 16px;

    @media screen and (min-width: 640px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (min-width: 1024px) {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  .raising-card {
    display: flex;
    flex-direction: column;
    padding: 20px;
    background: #FFFFFF;
    border: 1px solid #E5E7EB;
    border-radius: 12px;
    text-decoration: none;
    transition: all 0.2s ease;

    &:hover {
      border-color: #3B82F6;
      box-shadow: 0 4px 20px rgba(59, 130, 246, 0.12);

      .action-cta {
        background: #3B82F6;
        color: #FFFFFF;
      }

      .cta-icon {
        transform: translateX(4px);
      }
    }
  }

  // Card Header
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;
  }

  .token-avatar {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    overflow: hidden;
    background: #F9FAFB;
    border: 1px solid #E5E7EB;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .header-badges {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 500;

    &--live {
      background: #ECFDF5;
      color: #059669;
    }
  }

  .status-dot {
    width: 6px;
    height: 6px;
    background: #10B981;
    border-radius: 50%;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  .token-hero {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 8px;
    margin-top: 10px;
  }

  .hero-value {
    font-size: 20px;
    font-weight: 500;
    color: #111827;
  }

  .hero-unit {
    font-size: 12px;
    color: #6B7280;
  }

  .hero-change {
    font-size: 12px;
    font-weight: 500;
    color: #6B7280;

    &.up { color: #059669; }
    &.down { color: #DC2626; }
  }

  // Token Details
  .token-details {
    margin-bottom: 20px;
  }

  .token-name {
    font-size: 16px;
    font-weight: 500;
    color: #111827;
    margin: 0 0 4px 0;
    line-height: 1.3;
  }

  .token-symbol {
    font-size: 13px;
    font-weight: 500;
    color: #3B82F6;
  }

  // Stats Row
  .stats-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 0;
    border-top: 1px solid #F3F4F6;
    border-bottom: 1px solid #F3F4F6;
    margin-bottom: 16px;
  }

  .stat-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
  }

  .stat-value {
    font-size: 14px;
    font-weight: 500;
    color: #111827;
    margin-bottom: 2px;

    &--highlight {
      color: #3B82F6;
    }
  }

  .stat-label {
    font-size: 11px;
    font-weight: 400;
    color: #9CA3AF;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .stat-divider {
    width: 1px;
    height: 32px;
    background: #E5E7EB;
  }

  // Progress
  .progress-container {
    margin-bottom: 16px;
  }

  .progress-track {
    height: 4px;
    background: #E5E7EB;
    border-radius: 2px;
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    background: #3B82F6;
    border-radius: 2px;
    transition: width 0.4s ease;
  }

  // Footer
  .card-footer {
    margin-top: auto;
  }

  .action-cta {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 12px 16px;
    background: #F3F4F6;
    border-radius: 8px;
    transition: all 0.2s ease;
  }

  .cta-text {
    font-size: 13px;
    font-weight: 500;
    color: #374151;
  }

  .raising-card:hover .cta-text {
    color: #FFFFFF;
  }

  .cta-icon {
    width: 16px;
    height: 16px;
    color: #6B7280;
    transition: all 0.2s ease;
  }

  .raising-card:hover .cta-icon {
    color: #FFFFFF;
  }

  // Empty State
  .empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 64px 24px;
    background: #FFFFFF;
    border-radius: 12px;
    border: 1px solid #E5E7EB;
  }

  .empty-text {
    font-size: 14px;
    color: #6B7280;
    text-align: center;
    margin: 8px 0;
  }

  // Pagination
  .pagination-wrapper {
    display: flex;
    justify-content: center;
    padding: 32px 0;
  }
}

.token-offer-state {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
}

.token-closes {
  font-size: 11px;
  color: #64748b;
}
</style>
  